import { useEffect, useRef, useState } from 'react';
import { initialiseAccessButton } from '@workspace/access-button-launcher';
import { getApiBaseUrl, getCsrfToken } from '../../Services/axiosInstance';

interface AuthLike {
  isAuthenticated: boolean;
  user: { email: string | null; name: string | null; sid: string | null } | null;
}

export function useAccessButton(auth: AuthLike) {
  const [accessButtonDisabled, setAccessButtonDisabled] = useState(false);
  const isMountedRef = useRef(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buttonInitialized = useRef(false);

  const getTokenFn = async (): Promise<Response> => {
    const apiBaseUrl = getApiBaseUrl();
    const maxAttempts = 5;
    let attempt = 0;
    let lastError: unknown;

    while (attempt < maxAttempts) {
      if (!isMountedRef.current) break;
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/x-www-form-urlencoded',
        };
        const csrf = getCsrfToken();
        if (csrf) {
          headers['X-CSRF-TOKEN'] = csrf;
        }
        const res = await fetch(`${apiBaseUrl}/evo/button/token`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: '',
          signal: AbortSignal.timeout(10000),
        });

        if (!res.ok) {
          try {
            await res.clone().json();
          } catch {}
        }

        if (isMountedRef.current) {
          setAccessButtonDisabled(!res.ok);
        }

        return res;
      } catch (e) {
        lastError = e;
        attempt += 1;
        const delay = Math.min(1000 * Math.pow(1.5, attempt - 1), 5000);
        await new Promise<void>((r) => {
          retryTimeoutRef.current = setTimeout(r, delay);
        });
      }
    }

    console.error('Error fetching token:', lastError);
    if (isMountedRef.current) setAccessButtonDisabled(true);
    return new Response(
      JSON.stringify({
        error: 'service_unavailable',
        error_description: 'Token service temporarily unavailable',
        error_source: 'local',
      }),
      { status: 503, headers: { 'Content-Type': 'application/json' } }
    );
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      if (buttonInitialized.current) return;
      try {
        initialiseAccessButton({
          environment: (process.env.REACT_APP_ACCESS_BUTTON_ENV || 'preprod') as 'preprod',
          positions: {
            parentElement: '#access-button-anchor',
            desktop: { top: 5, left: 2 },
            mobile: { bottom: 2, left: 2 },
          },
          closedZindex: 10000000,
          getToken: async () => {
            try {
              return await getTokenFn();
            } catch (error) {
              console.error('Access button token retrieval error:', error);
              return new Response(
                JSON.stringify({
                  error: 'service_unavailable',
                  error_description: 'Token service temporarily unavailable',
                  error_source: 'local',
                }),
                { status: 503, headers: { 'Content-Type': 'application/json' } }
              );
            }
          },
        });
        buttonInitialized.current = true;
      } catch (e) {
        console.error('Access Button init failed:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.user]);

  return { accessButtonDisabled };
}
